import { useState } from 'react';
import {
    useGetSpaCallsByHotelQuery,
    useGetSpaCallByIdQuery,
    ISpaCall,
} from '../../../../../store/excecutive/executiveApi';

const HOTEL_SUB = 'e28524d4-60b1-70c5-0228-8f8824193e20';

// Format Call ID to a shorter, more usable format (last 8 characters)
const formatCallId = (callId: string) => {
    if (!callId) return 'N/A';
    const shortId = callId.length > 8 ? callId.slice(-8) : callId;
    return shortId.toUpperCase();
};

// Call Transfer Details Modal Component
const CallTransferModal = ({
    call,
    isOpen,
    onClose,
    isLoading,
}: {
    call: ISpaCall | null;
    isOpen: boolean;
    onClose: () => void;
    isLoading?: boolean;
}) => {
    if (!isOpen) return null;

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-[#1B1B1B] rounded-lg shadow-xl max-w-4xl w-full p-6">
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm">
                            Loading call details...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!call) return null;

    const aiSummary = call.aiSummary;
    const isTransferred = call.aiSummary?.callOutcome === 'transferred';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto dark:bg-[#1B1B1B]">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-[#1B1B1B] border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Call Details
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Call Basic Info - Detailed Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                Call ID
                            </span>
                            <span className="text-sm font-mono font-semibold text-gray-900 dark:text-white">
                                {formatCallId(call.callId)}
                            </span>
                            <span className="text-xs text-gray-400 ml-2">
                                ({call.callId})
                            </span>
                        </div>
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                Status
                            </span>
                            <span
                                className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                    call.status === 'completed'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : call.status === 'transferred'
                                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                          : call.status === 'failed'
                                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }`}
                            >
                                {call.status || 'unknown'}
                            </span>
                        </div>
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                Duration
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white">
                                {call.durationSec
                                    ? `${Math.floor(call.durationSec / 60)}m ${call.durationSec % 60}s`
                                    : 'N/A'}
                            </span>
                        </div>
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                Start Date & Time
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white">
                                {call.startedAt
                                    ? new Date(call.startedAt).toLocaleString()
                                    : 'N/A'}
                            </span>
                        </div>
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                End Date & Time
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white">
                                {call.endedAt
                                    ? new Date(call.endedAt).toLocaleString()
                                    : 'N/A'}
                            </span>
                        </div>
                        <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                End Reason
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white capitalize">
                                {call.endReason || 'N/A'}
                            </span>
                        </div>
                        {call.fromNumber && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    From Number
                                </span>
                                <span className="text-sm text-gray-900 dark:text-white">
                                    {call.fromNumber}
                                </span>
                            </div>
                        )}
                        {call.toNumber && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    To Number
                                </span>
                                <span className="text-sm text-gray-900 dark:text-white">
                                    {call.toNumber}
                                </span>
                            </div>
                        )}
                        {call.hotelSub && (
                            <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    Hotel Sub
                                </span>
                                <span className="text-sm text-gray-900 dark:text-white font-mono">
                                    {call.hotelSub.substring(0, 20)}...
                                </span>
                            </div>
                        )}
                    </div>

                    {/* AI Summary Section */}
                    {aiSummary && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                AI Analysis
                            </h3>

                            {/* Summary */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                                    Summary
                                </h4>
                                <p className="text-sm text-gray-900 dark:text-white bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800">
                                    {aiSummary.summary || 'No summary available'}
                                </p>
                            </div>

                            {/* Transfer Information - Only show if transferred */}
                            {isTransferred && (
                                <>
                                    {/* Reason for Transfer */}
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                                            Reason for Transfer
                                        </h4>
                                        <p className="text-sm text-gray-900 dark:text-white bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md border border-yellow-200 dark:border-yellow-800">
                                            {aiSummary.transferReason || 'Not specified'}
                                        </p>
                                    </div>

                                    {/* Desired Action / Outcome */}
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                                            Desired Action / Outcome
                                        </h4>
                                        <p className="text-sm text-gray-900 dark:text-white bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800">
                                            {aiSummary.desiredAction ||
                                                'No specific action provided'}
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Additional AI Summary Details */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div>
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                        Call Outcome
                                    </span>
                                    <span className="text-sm text-gray-900 dark:text-white capitalize">
                                        {aiSummary.callOutcome || 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                        Booking Successful
                                    </span>
                                    <span
                                        className={`text-sm font-semibold ${
                                            aiSummary.bookingSuccessful
                                                ? 'text-green-600 dark:text-green-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}
                                    >
                                        {aiSummary.bookingSuccessful ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                        Frustration Detected
                                    </span>
                                    <span
                                        className={`text-sm font-semibold ${
                                            aiSummary.frustrationDetected
                                                ? 'text-red-600 dark:text-red-400'
                                                : 'text-green-600 dark:text-green-400'
                                        }`}
                                    >
                                        {aiSummary.frustrationDetected ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                {aiSummary.bookingFailureReason && (
                                    <div>
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                            Booking Failure Reason
                                        </span>
                                        <span className="text-sm text-gray-900 dark:text-white">
                                            {aiSummary.bookingFailureReason}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Key Points */}
                            {aiSummary.keyPoints &&
                                aiSummary.keyPoints.length > 0 && (
                                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                                            Key Points
                                        </h4>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-900 dark:text-white bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800">
                                            {aiSummary.keyPoints.map(
                                                (point, index) => (
                                                    <li key={index}>{point}</li>
                                                ),
                                            )}
                                        </ul>
                                    </div>
                                )}

                            {aiSummary.generatedAt && (
                                <div className="pt-2 text-xs text-gray-400 dark:text-gray-500">
                                    Summary generated:{' '}
                                    {new Date(
                                        aiSummary.generatedAt,
                                    ).toLocaleString()}
                                </div>
                            )}
                        </div>
                    )}

                    {/* No AI Summary */}
                    {!aiSummary && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                AI summary not yet generated for this call.
                            </p>
                        </div>
                    )}

                    {/* Booking Information */}
                    {call.booking &&
                        Object.keys(call.booking).length > 0 && (
                            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Booking Information
                                </h3>
                                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-700">
                                    <pre className="text-xs text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
                                        {JSON.stringify(call.booking, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}

                    {/* Transcript Preview */}
                    {call.transcriptText && (
                        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Transcript
                            </h3>
                            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                                <p className="text-xs text-gray-900 dark:text-white whitespace-pre-wrap">
                                    {call.transcriptText.substring(0, 2000)}
                                    {call.transcriptText.length > 2000 && '...'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 dark:bg-[#1B1B1B] border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Call Management Component
const CallManagement = () => {
    const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterOutcome, setFilterOutcome] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const {
        data: calls = [],
        isLoading: loadingCalls,
        refetch: refetchCalls,
    } = useGetSpaCallsByHotelQuery({
        hotelSub: HOTEL_SUB,
        limit: 50,
    });

    const { data: callDetails, isLoading: loadingCallDetails } =
        useGetSpaCallByIdQuery(selectedCallId || '', {
            skip: !selectedCallId || !isModalOpen,
        });

    // Open modal with call details
    const openCallModal = (call: ISpaCall) => {
        setSelectedCallId(call.callId);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedCallId(null);
    };

    // Filter calls based on selected filters
    const filteredCalls = (calls || []).filter((call) => {
        // Status filter
        if (filterStatus !== 'all' && call.status !== filterStatus) {
            return false;
        }
        // Outcome filter
        if (
            filterOutcome !== 'all' &&
            call.aiSummary?.callOutcome !== filterOutcome
        ) {
            return false;
        }
        // Search filter (by Call ID)
        if (
            searchTerm &&
            !formatCallId(call.callId)
                .includes(searchTerm.toUpperCase())
        ) {
            return false;
        }
        return true;
    });

    // Get unique statuses and outcomes for filter dropdowns
    const uniqueStatuses = Array.from(
        new Set((calls || []).map((c) => c.status).filter(Boolean)),
    );
    const uniqueOutcomes = Array.from(
        new Set(
            (calls || [])
                .map((c) => c.aiSummary?.callOutcome)
                .filter(Boolean),
        ),
    );

    // Find the call from the list if callDetails is not yet loaded
    const listCall = calls?.find((c) => c.callId === selectedCallId);
    const modalCall = callDetails || listCall || null;

    return (
        <div className="w-full">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Call Management
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    View and manage recent calls
                </p>
            </div>

            {/* Calls List Section */}
            <div className="bg-white dark:bg-[#1B1B1B] rounded-lg shadow-md p-6 border border-gray-200 dark:border-transparent">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Recent Calls
                    </h3>
                    <button
                        onClick={() => refetchCalls()}
                        className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800 transition-colors text-sm"
                        disabled={loadingCalls}
                    >
                        {loadingCalls ? 'Loading...' : 'Refresh'}
                    </button>
                </div>

                {/* Filters */}
                <div className="mb-4 flex flex-wrap gap-4 items-end">
                    {/* Search by Call ID */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Search Call ID
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Call ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-[#333333] dark:text-white"
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="min-w-[150px]">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                        </label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-[#333333] dark:text-white"
                        >
                            <option value="all">All Statuses</option>
                            {uniqueStatuses.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Outcome Filter */}
                    <div className="min-w-[150px]">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Outcome
                        </label>
                        <select
                            value={filterOutcome}
                            onChange={(e) => setFilterOutcome(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-[#333333] dark:text-white"
                        >
                            <option value="all">All Outcomes</option>
                            {uniqueOutcomes.map((outcome) => (
                                <option key={outcome} value={outcome}>
                                    {outcome}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Clear Filters */}
                    {(filterStatus !== 'all' ||
                        filterOutcome !== 'all' ||
                        searchTerm) && (
                        <button
                            onClick={() => {
                                setFilterStatus('all');
                                setFilterOutcome('all');
                                setSearchTerm('');
                            }}
                            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                {loadingCalls ? (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm">
                            Loading calls...
                        </p>
                    </div>
                ) : !calls || calls.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                        No calls found
                    </p>
                ) : filteredCalls.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                        No calls match the selected filters
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                            Showing {filteredCalls.length} of {calls.length}{' '}
                            calls
                        </div>
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-[#333333]">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Call ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Date & Time
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Duration
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Outcome
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Call Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-[#1B1B1B] divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredCalls.map((call) => (
                                    <tr
                                        key={call.callId}
                                        className="hover:bg-gray-50 dark:hover:bg-[#333333]"
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white font-semibold">
                                            {formatCallId(call.callId)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {call.startedAt
                                                ? new Date(
                                                      call.startedAt,
                                                  ).toLocaleString()
                                                : 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {call.durationSec
                                                ? `${Math.floor(call.durationSec / 60)}m ${call.durationSec % 60}s`
                                                : 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    call.status === 'completed'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : call.status ===
                                                            'transferred'
                                                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                          : call.status ===
                                                              'failed'
                                                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                            : call.status ===
                                                                  'hangup'
                                                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                                              : call.status ===
                                                                    'in_progress'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                : call.status ===
                                                                      'queued'
                                                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                                }`}
                                            >
                                                {call.status === 'queued'
                                                    ? 'completed'
                                                    : call.status || 'unknown'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white capitalize">
                                            {call.aiSummary?.callOutcome ||
                                                'N/A'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() =>
                                                    openCallModal(call)
                                                }
                                                className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Call Transfer Modal */}
            <CallTransferModal
                call={modalCall}
                isOpen={isModalOpen}
                onClose={closeModal}
                isLoading={loadingCallDetails}
            />
        </div>
    );
};

export default CallManagement;
